<script setup lang="ts">
import type { ColDef } from "ag-grid-community";
import AppGrid from "../../components/AppGrid.vue";
import { useModalStore } from "../../stores/modalStore.ts";
import { formatCalendarDateForDisplay } from "../../utils/dateFormatting.ts";
import type { BulkOrderCreationRow } from "./bulkOrderCreationMock.ts";

defineProps<{ identifier: string; rows: BulkOrderCreationRow[] }>();

const modalStore = useModalStore();

function renderSkuCell(parameters: { value: string }): HTMLElement {
  const strong = document.createElement("strong");
  strong.textContent = parameters.value;
  return strong;
}

function renderStatusCell(parameters: {
  value: BulkOrderCreationRow["status"];
}): HTMLElement {
  const pill = document.createElement("span");
  pill.className = `bulk-order-modal-status-pill bulk-order-modal-status-pill-${parameters.value.toLowerCase()}`;
  pill.textContent = parameters.value;
  return pill;
}

const columnDefs: ColDef<BulkOrderCreationRow>[] = [
  {
    field: "sku",
    headerName: "SKU",
    flex: 1.2,
    minWidth: 140,
    cellRenderer: renderSkuCell,
  },
  { field: "description", headerName: "Description", flex: 2, minWidth: 180 },
  {
    field: "shipDate",
    headerName: "Ship Date",
    width: 110,
    valueFormatter: ({ value }) =>
      formatCalendarDateForDisplay(typeof value === "string" ? value : ""),
  },
  {
    field: "shipToWarehouse",
    headerName: "Warehouse",
    flex: 1.4,
    minWidth: 150,
  },
  { field: "requestedUnits", headerName: "Qty", width: 90 },
  {
    field: "status",
    headerName: "Status",
    width: 110,
    cellRenderer: renderStatusCell,
  },
];

const defaultColDef: ColDef = {
  sortable: true,
  filter: false,
  resizable: true,
};
</script>

<template>
  <div class="bulk-order-modal">
    <div class="modal-header draggable">
      <span class="modal-header-title">Full Intermediate Order</span>
      <button
        class="modal-header-close"
        type="button"
        aria-label="Close full Intermediate Order"
        @click="modalStore.close()"
      >
        &times;
      </button>
    </div>
    <div class="modal-body bulk-order-modal-body">
      <div class="bulk-order-modal-summary">
        <span>Intermediate Order</span>
        <strong>{{ identifier }}</strong>
        <span>{{ rows.length }} SKUs</span>
      </div>
      <div class="bulk-order-modal-grid">
        <AppGrid
          id="bulk-order-creation-full-io-grid"
          :column-defs="columnDefs"
          :row-data="rows"
          :default-col-def="defaultColDef"
          :fill-container="true"
          :auto-size-columns="false"
          :default-row-height="20"
          :get-row-height="() => 20"
          :no-rows-overlay-component-params="{
            getTitle: () => 'No SKU lines found.',
          }"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.bulk-order-modal {
  display: flex;
  height: 100%;
  flex-direction: column;
}

.bulk-order-modal-body {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 0.85rem;
  padding: 1rem 1.25rem 1.25rem;
}

.bulk-order-modal-summary {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.5rem;
  color: var(--text-secondary, #5d6878);
  font-size: 0.85rem;
}

.bulk-order-modal-summary strong {
  color: var(--text-primary, #172033);
  font-size: 1rem;
}

.bulk-order-modal-grid {
  min-height: 0;
  flex: 1;
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 8px;
  overflow: hidden;
}

.bulk-order-modal-status-pill {
  display: inline-flex;
  border-radius: 999px;
  padding: 0 4px;
  font-size: inherit;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.bulk-order-modal-status-pill-ready {
  background: #d8f5e4;
  color: #166534;
}

.bulk-order-modal-status-pill-review {
  background: #fff3cd;
  color: #8a6100;
}
</style>
